import { c as _c } from "react/compiler-runtime";
import { IcoPasskey40 } from '@onefootprint/icons';
import { Button, PageShell } from '@onefootprint/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import RegisterPasskey from '@/idv2/features/liveness/register-passkey';
import useD2pSession from '@/idv2/features/transfer/hooks/use-d2p-session';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import createHandoffUrlAuth from '@/idv2/utils/create-handoff-url-auth';
import { checkDeviceInfo, type DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'passkey-offer'
});
const passkeyOfferSearchSchema = z.object({
  authToken: z.string(),
  validationToken: z.string()
});
export const Route = createFileRoute('/identify/passkey-offer')({
  validateSearch: passkeyOfferSearchSchema,
  loader: async () => {
    let device: DeviceInfo | undefined;
    try {
      device = await checkDeviceInfo();
    } catch (error) {
      logWarn('Unable to collect device info', error);
    }
    return {
      device
    };
  },
  component: PasskeyOfferRoute
});
function PasskeyOfferRoute() {
  const $ = _c(46);
  const {
    authToken,
    validationToken
  } = Route.useSearch();
  const {
    device
  } = Route.useLoaderData();
  const navigate = useNavigate();
  const footprint = useFootprint();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "identify.passkeyOffer"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation("idv2", t0);
  const [showInlinePasskey, setShowInlinePasskey] = useState(false);
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;
  let t1;
  if ($[1] !== footprint.navigation || $[2] !== validationToken) {
    t1 = () => {
      footprint.navigation.identifyComplete(validationToken);
    };
    $[1] = footprint.navigation;
    $[2] = validationToken;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const proceedToDone = t1;
  let t2;
  if ($[4] !== device) {
    t2 = device ?? {
      type: "unknown",
      hasSupportForWebauthn: false
    };
    $[4] = device;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== authToken || $[7] !== proceedToDone || $[8] !== t2) {
    t3 = {
      authToken,
      device: t2,
      onFinish: proceedToDone,
      onCancel: proceedToDone
    };
    $[6] = authToken;
    $[7] = proceedToDone;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const d2p = useD2pSession(t3);
  const scopedAuthToken = d2p.state.scopedAuthToken;
  let t4;
  if ($[10] !== authToken || $[11] !== device || $[12] !== i18n || $[13] !== navigate || $[14] !== scopedAuthToken || $[15] !== validationToken) {
    t4 = () => {
      if (!isInIframe && device) {
        setShowInlinePasskey(true);
        return;
      }
      const url = createHandoffUrlAuth({
        authToken: scopedAuthToken,
        language: i18n.language
      });
      if (!url || !device) {
        return;
      }
      const isMobile = device.type === "mobile" || device.type === "tablet";
      const tab = window.open(url.toString(), "_blank", isMobile ? undefined : "height=800px,width=600px,location=no,menubar=no,status=no,toolbar=no,left=100px,top=100px");
      if (tab) {
        (window as Window & {
          __fp_passkey_window?: Window;
        }).__fp_passkey_window = tab;
        navigate({
          to: "/identify/passkey-processing",
          search: {
            authToken,
            validationToken,
            scopedAuthToken
          }
        });
      } else {
        logWarn("Could not open passkey registration window");
      }
    };
    $[10] = authToken;
    $[11] = device;
    $[12] = i18n;
    $[13] = navigate;
    $[14] = scopedAuthToken;
    $[15] = validationToken;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleAddPasskey = t4;
  let t5;
  if ($[17] !== proceedToDone) {
    t5 = () => {
      logInfo("User skipped passkey registration");
      proceedToDone();
    };
    $[17] = proceedToDone;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleSkip = t5;
  if (showInlinePasskey && device) {
    let t6;
    if ($[19] !== authToken || $[20] !== device || $[21] !== proceedToDone) {
      t6 = <RegisterPasskey deviceInfo={device} isInIframe={false} token={authToken} actionKind="add_primary" onComplete={proceedToDone} />;
      $[19] = authToken;
      $[20] = device;
      $[21] = proceedToDone;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  const isLoading = isInIframe && (d2p.state.isGenerating || !scopedAuthToken);
  let t6;
  if ($[23] !== t) {
    t6 = t(_temp);
    $[23] = t;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t) {
    t7 = t(_temp2);
    $[25] = t;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoPasskey40 color="primary" />;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t) {
    t9 = t(_temp3);
    $[28] = t;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== handleAddPasskey || $[31] !== isLoading || $[32] !== t9) {
    t10 = <Button onClick={handleAddPasskey} fullWidth={true} size="large" disabled={isLoading} loading={isLoading}>{t9}</Button>;
    $[30] = handleAddPasskey;
    $[31] = isLoading;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t) {
    t11 = t(_temp4);
    $[34] = t;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== handleSkip || $[37] !== t11) {
    t12 = <Button onClick={handleSkip} fullWidth={true} size="large" variant="secondary">{t11}</Button>;
    $[36] = handleSkip;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t10 || $[40] !== t12) {
    t13 = <div className="flex flex-col gap-3 pt-4">{t10}{t12}</div>;
    $[39] = t10;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t6 || $[44] !== t7) {
    t14 = <PageShell title={t6} subtitle={t7} headerIcon={t8}>{t13}</PageShell>;
    $[42] = t13;
    $[43] = t6;
    $[44] = t7;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  return t14;
}
function _temp4($_2) {
  return $_2.skip;
}
function _temp3($_1) {
  return $_1.add;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp($) {
  return $.title;
}